import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Form, Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import AddIcon from '@mui/icons-material/Add';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from 'components/TextField';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Stack from '@mui/material/Stack';
import ServiceTypeSelect from 'components/ServiceTypeSelect';

import { useErrorHandler } from 'lib';
import { useDialog } from 'components/Dialog';

import { AddServiceDialogMutation } from '__generated__/AddServiceDialogMutation.graphql';
import { AddServiceDialog_serviceGroup$key } from '__generated__/AddServiceDialog_serviceGroup.graphql';
import { ServiceType } from '__generated__/ServiceRow_service.graphql';

type FormFields = {
  name: string;
  description: string;
  placeholder: string;
  type: ServiceType | null;
};

type Props = {
  serviceGroup: AddServiceDialog_serviceGroup$key;
  connectionId: string;
};

const initial = {
  name: '',
  description: '',
  placeholder: '',
  type: 'SOLO' as ServiceType,
};

const AddServiceDialog: FC<Props> = ({ connectionId, ...props }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();

  const { id } = useFragment(
    graphql`
      fragment AddServiceDialog_serviceGroup on ServiceGroup {
        id
      }
    `,
    props.serviceGroup
  );

  const [add, adding] = useMutation<AddServiceDialogMutation>(graphql`
    mutation AddServiceDialogMutation(
      $id: ID!
      $input: CreateServiceInput!
      $connections: [ID!]!
    ) {
      addServiceToServiceGroup(id: $id, input: $input) {
        node
          @appendNode(connections: $connections, edgeTypeName: "ServiceEdge") {
          ...ServiceRow_service
        }
      }
    }
  `);

  const handleSubmit = useCallback(
    (input: FormFields) => {
      add({
        variables: {
          id,
          input,
          connections: [connectionId],
        },

        ...errorHandler({
          onCompleted: close,
        }),
      });
    },
    [add, close, connectionId, errorHandler, id]
  );

  return (
    <Formik<FormFields> initialValues={initial} onSubmit={handleSubmit}>
      <Form>
        <DialogTitle>Nauja paslauga</DialogTitle>

        <DialogContent dividers>
          <DialogContentText sx={{ mb: 2 }}>
            Pridėkite naują paslaugą į paslaugų paketą
          </DialogContentText>

          <Stack spacing={4}>
            <TextField autoFocus name="name" label="Pavadinimas" fullWidth />
            <TextField
              fullWidth
              multiline
              name="description"
              label="Aprašymas"
            />

            <TextField fullWidth name="placeholder" label="Placeholder" />
            <ServiceTypeSelect name="type" label="Tipas" />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={close} variant="text">
            Atšaukti
          </Button>

          <LoadingButton
            loading={adding}
            type="submit"
            variant="contained"
            startIcon={<AddIcon />}>
            Pridėti
          </LoadingButton>
        </DialogActions>
      </Form>
    </Formik>
  );
};

export default AddServiceDialog;
